import { DatePicker, DateUtil, Switch } from '@aon/aon-tws-common-ui';
import { MessageHelper } from '@aon/aon-tws-common-ui/build/lib/components/Common';
import * as React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { IEmailTemplate, IValidationErrorCollection } from '../../models';
import { AudiencePropertiesUnderValidation } from '../../models/AudiencePropertiesUnderValidation';
import { ValidationErrorHelper } from '../../utilities/ValidationErrorHelper';
import { AudienceMode } from '../audiences/AudienceMode';
import { Col3Col9, LabelAbove, WarningModal } from '../common';
import * as AudienceValidation from './AudienceValidation';
import { ErrorMessageLabelNoIcon, SwitchWithLabelContainer } from './SharedComponents';

const DatePickersContainer = styled.div`
	display: flex;
	@media (min-width: 0) and (max-width: 1500px) {
		flex-direction: column;
	}
`;
DatePickersContainer.displayName = 'DatePickersContainer';

const OpenDatePicker = styled(DatePicker)`
	margin-right: 30px;
`;
OpenDatePicker.displayName = 'OpenDatePicker';

const OpenDateContainer = styled.div`
	margin-bottom: 15px;
`;
OpenDateContainer.displayName = 'OpenDateContainer';

const CloseDateContainer = styled.div`
	margin-bottom: 0;
`;
CloseDateContainer.displayName = 'CloseDateContainer';

const PickerLabel = styled.div`
	font-size: 12px;
	font-weight: 700;
	margin-bottom: 5px;
`;
PickerLabel.displayName = 'PickerLabel';

const datePickerFormat = 'MMM D, YYYY [at] h:mma';

export interface IOpenCloseDateRangeProps {
	closeOn?: string;
	openImmediately: boolean;
	openOn?: string;
	reminderEmails?: IEmailTemplate[];
	mode: AudienceMode;
	validationError?: IValidationErrorCollection;
	onChange: (isOpenImmediately: boolean, openOn?: string, closeOn?: string) => void;
}

export class OpenCloseDateRange extends React.Component<
	IOpenCloseDateRangeProps,
	{
		warningModalOpen: boolean;
	}
> {
	constructor(props: IOpenCloseDateRangeProps) {
		super(props);

		this.state = {
			warningModalOpen: false,
		};

		this.getValidationError = this.getValidationError.bind(this);
		this.handleOpenImmediately = this.handleOpenImmediately.bind(this);
		this.onOpenOnChange = this.onOpenOnChange.bind(this);
		this.onCloseOnChange = this.onCloseOnChange.bind(this);
		this.onOpenOnClear = this.onOpenOnClear.bind(this);
		this.onCloseOnClear = this.onCloseOnClear.bind(this);
		this.closeModal = this.closeModal.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IOpenCloseDateRangeProps) {
		if (
			this.props.openOn !== nextProps.openOn ||
			this.props.closeOn !== nextProps.closeOn ||
			this.props.openImmediately !== nextProps.openImmediately
		) {
			this.validate(nextProps);
		}
	}
	public validate(props: IOpenCloseDateRangeProps) {
		const { openOn, closeOn } = props;

		this.setState({ warningModalOpen: !this.reminderDatesValid(openOn, closeOn) });
	}

	public render() {
		const { openOn, closeOn, mode } = this.props;
		const { warningModalOpen } = this.state;

		const openImmediatelyCloseOn = closeOn !== undefined ? closeOn : undefined;

		const validationErrorHelper = new ValidationErrorHelper<AudiencePropertiesUnderValidation>(this.getValidationError());
		return (
			<Col3Col9 label={ContentConstants.AUDIENCE_OPENCLOSEDATES_TITLE} className='tws-survey-audience-open-close-dates' clearHeight='40px'>
				<LabelAbove type='input' textIsHtml={true} text={ContentConstants.INSTRUCTIONALTEXT_AUDIENCE_OPEN_CLOSE_DATES} />
				{mode === AudienceMode.Edit && (
					<SwitchWithLabelContainer>
						<Switch checked={this.isOpenImmediately()} getValue={this.handleOpenImmediately} />
						<div>{ContentConstants.AUDIENCE_OPENIMMEDIATELY_TOGGLE}</div>
					</SwitchWithLabelContainer>
				)}

				<DatePickersContainer>
					{!this.isOpenImmediately() && (
						<OpenDateContainer className='tws-open-date-container'>
							<PickerLabel>Open Date</PickerLabel>
							<OpenDatePicker
								className={'tws-open-date-picker'}
								defaultGap={0}
								value={openOn}
								textInputPlaceholder={ContentConstants.DATEPICKER_OPEN_DATE}
								clearText={ContentConstants.DATEPICKER_CLEARTEXT}
								enabledStartDate={moment()}
								onChange={this.onOpenOnChange}
								onClear={this.onOpenOnClear}
								dateFormat={datePickerFormat}
								headerLabel='Open Date'
								error={!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.OpenOn)}
								message={validationErrorHelper.Errors(AudiencePropertiesUnderValidation.OpenOn)}
							/>
						</OpenDateContainer>
					)}
					<CloseDateContainer className='tws-close-date-container'>
						<PickerLabel>Close Date</PickerLabel>
						<DatePicker
							className={'tws-close-date-picker'}
							defaultGap={0}
							value={openImmediatelyCloseOn}
							textInputPlaceholder={ContentConstants.DATEPICKER_CLOSE_DATE}
							clearText={ContentConstants.DATEPICKER_CLEARTEXT}
							enabledStartDate={openOn ? moment(openOn) : moment()}
							onChange={this.onCloseOnChange}
							onClear={this.onCloseOnClear}
							dateFormat={datePickerFormat}
							headerLabel='Close Date'
							error={!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.CloseOn)}
							message={validationErrorHelper.Errors(AudiencePropertiesUnderValidation.CloseOn)}
						/>
					</CloseDateContainer>
				</DatePickersContainer>

				{!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.OpenCloseDates) && (
					<ErrorMessageLabelNoIcon className='tws-open-close-dates-error-message'>
						{MessageHelper.getFormattedErrorMessages(validationErrorHelper.Errors(AudiencePropertiesUnderValidation.OpenCloseDates))}
					</ErrorMessageLabelNoIcon>
				)}

				{warningModalOpen && (
					<WarningModal
						isOpen={warningModalOpen}
						secondaryButtonText={ContentConstants.BUTTONS_CLOSE}
						onClose={this.closeModal}
						contentHeadline={ContentConstants.WARNING_MODAL_OPEN_CLOSE_DATES_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_OPEN_CLOSE_DATES_BODY}
					/>
				)}
			</Col3Col9>
		);
	}

	public getValidationError() {
		const { openOn, closeOn, openImmediately, validationError } = this.props;

		const openCloseDateValidationError = AudienceValidation.validateAudienceOpenCloseDates(openOn, closeOn, openImmediately);
		let combinedValidationError = validationError;
		if (openCloseDateValidationError) {
			if (combinedValidationError) {
				combinedValidationError[AudiencePropertiesUnderValidation.OpenCloseDates] =
					openCloseDateValidationError[AudiencePropertiesUnderValidation.OpenCloseDates];
			} else {
				combinedValidationError = openCloseDateValidationError;
			}
		}
		return combinedValidationError;
	}

	public handleOpenImmediately(openImmediately: any): boolean {
		const { openOn, closeOn, onChange } = this.props;

		if (onChange) {
			onChange(openImmediately, openImmediately === true ? undefined : openOn, closeOn);
		}

		return true;
	}

	public reminderDatesValid(openOn?: string, closeOn?: string) {
		const { reminderEmails } = this.props;

		let invalid = true;
		if (reminderEmails && reminderEmails.length > 0) {
			reminderEmails.forEach((reminderEmail) => {
				if (invalid && closeOn && reminderEmail.scheduledFor) {
					const closeOnMoment = moment(closeOn);
					const openOnMoment = moment(openOn);
					if (this.isOpenImmediately()) {
						openOnMoment.set({ minute: 0, second: 0, millisecond: 0 });
					}

					const scheduledFor = moment(reminderEmail.scheduledFor);
					if (scheduledFor.valueOf() < openOnMoment.valueOf() || scheduledFor.valueOf() > closeOnMoment.valueOf()) {
						invalid = false;
					}
				}
			});
		}
		return invalid;
	}

	private onOpenOnChange(date?: any) {
		const { closeOn, onChange } = this.props;

		if (onChange) {
			onChange(this.isOpenImmediately(), this.getUtcDate(date), closeOn);
		}
	}

	private onCloseOnChange(date?: any) {
		const { openOn, onChange } = this.props;

		if (onChange) {
			const openImmediately = this.isOpenImmediately();
			onChange(openImmediately, openOn, this.getUtcDate(date));
		}
	}

	private onOpenOnClear() {
		const { closeOn, onChange } = this.props;

		if (onChange) {
			onChange(this.isOpenImmediately(), undefined, closeOn);
		}
	}

	private onCloseOnClear() {
		const { openOn, onChange } = this.props;

		if (onChange) {
			onChange(this.isOpenImmediately(), openOn, undefined);
		}
	}

	private closeModal() {
		this.setState({ warningModalOpen: false });
	}

	private isOpenImmediately() {
		return this.props.openImmediately || false;
	}

	private getUtcDate(dateString?: string) {
		if (dateString) {
			return DateUtil.getUtcDateTime(new Date(dateString)).toString();
		}
		return undefined;
	}
}
